import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Download, FileSignature, Eye } from "lucide-react";
import { toast } from "sonner";
import JeaCard from "@/components/jea/JeaCard";
import EmptyState from "@/components/jea/EmptyState";
import Skeleton from "@/components/jea/Skeleton";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { downloadAuthFile } from "@/lib/downloadFile";
import { centsToUSD, shortDate } from "@/lib/dates";
import { cn } from "@/lib/utils";

const HeaderCell = ({ children }) => (
  <th className="jea-caption text-text-secondary p-4 text-left">{children}</th>
);

const IconAction = ({ Icon, label, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    className="w-8 h-8 rounded-[6px] border border-border-default text-text-secondary hover:text-accent-pink hover:border-accent-pink/60 transition-colors inline-flex items-center justify-center"
    data-testid={testid}
  >
    <Icon size={14} />
  </button>
);

const RenewalKindBadge = () => (
  <span
    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-[0.06em] uppercase border border-accent-pink/40 bg-accent-pink/10 text-accent-pink"
    data-testid="reports-tab-badge-renewal"
  >
    <FileSignature size={10} /> Renewal
  </span>
);

const AssetKindBadge = () => (
  <span
    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-[0.06em] uppercase border border-border-default bg-bg-tertiary text-text-secondary"
    data-testid="reports-tab-badge-asset"
  >
    <FileText size={10} /> Asset Discovery
  </span>
);

/**
 * Re-generate + download a renewal PDF. The server persists a new report row
 * on every call so the history reflects each fetch.
 */
const downloadRenewalPdf = async (documentType, debtorId, title) => {
  try {
    const res = await apiClient.post(
      `/renewal/generate/${documentType}`,
      { debtor_id: debtorId },
      { responseType: "blob" }
    );
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${documentType}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success(`Downloaded ${title || "renewal document"}.`);
  } catch (e) {
    toast.error(formatApiError(e, "Couldn't download renewal document."));
  }
};

const ReportsTab = ({ debtorId }) => {
  const nav = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get(`/debtors/${debtorId}/reports`);
        if (alive) setReports(data.reports || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [debtorId]);

  if (loading) {
    return (
      <div className="space-y-3" data-testid="reports-tab-loading">
        {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="No reports yet"
        description="Every completed broker query and every generated renewal document appears here."
        testid="reports-tab-empty"
      />
    );
  }

  return (
    <JeaCard className="p-0 overflow-hidden" data-testid="reports-tab">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border-default">
            <HeaderCell>Kind</HeaderCell>
            <HeaderCell>Report Date</HeaderCell>
            <HeaderCell>Brokers Run</HeaderCell>
            <HeaderCell>Assets Found</HeaderCell>
            <HeaderCell>Changes Detected</HeaderCell>
            <HeaderCell>Cost</HeaderCell>
            <HeaderCell>Actions</HeaderCell>
          </tr>
        </thead>
        <tbody>
          {reports.map((r) => {
            const isRenewal = r.type === "renewal_document";
            const isHalt = !!r.hard_override;
            const stopClick = (e) => e.stopPropagation();
            const onRowClick = () => {
              if (isRenewal) return; // renewal rows don't navigate
              nav(`/debtors/${debtorId}/reports/${r.id}`);
            };
            return (
              <motion.tr
                key={r.id}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}
                className={cn(
                  "border-b border-border-default hover:bg-bg-tertiary transition-colors",
                  !isRenewal && "cursor-pointer",
                  isHalt && "bg-danger/5",
                )}
                onClick={onRowClick}
                data-testid={`reports-tab-row-${r.id}`}
              >
                <td className="p-4">
                  {isRenewal ? <RenewalKindBadge /> : <AssetKindBadge />}
                  {isRenewal && r.title && (
                    <div className="text-[11px] text-text-secondary mt-1.5 truncate max-w-[220px]">
                      {r.title}
                    </div>
                  )}
                </td>
                <td className="p-4 jea-date text-[13px]">{shortDate(r.generated_at)}</td>
                <td className="p-4 jea-mono text-white font-semibold">
                  {isRenewal ? <span className="text-text-muted">—</span> : (r.broker_count ?? 0)}
                </td>
                <td className="p-4 jea-mono text-white font-semibold">
                  {isRenewal ? <span className="text-text-muted">—</span> : (r.assets_found_count ?? 0)}
                </td>
                <td className="p-4">
                  {isRenewal ? (
                    <span className="jea-mono text-text-muted">—</span>
                  ) : r.changes_detected_count > 0 ? (
                    <span className="jea-mono font-bold text-warning">{r.changes_detected_count}</span>
                  ) : (
                    <span className="jea-mono text-text-muted">0</span>
                  )}
                </td>
                <td className="p-4 jea-mono text-accent-pink font-bold">
                  {isRenewal ? <span className="text-text-muted">—</span> : centsToUSD(r.total_cost_cents || 0)}
                </td>
                <td className="p-4" onClick={stopClick}>
                  <div className="flex items-center gap-2">
                    {isRenewal ? (
                      <IconAction
                        Icon={Download}
                        label={`Download ${r.title || "renewal document"}`}
                        onClick={() => downloadRenewalPdf(r.document_type, debtorId, r.title)}
                        testid={`reports-tab-action-renewal-pdf-${r.id}`}
                      />
                    ) : (
                      <>
                        <IconAction Icon={Eye} label="View Report"
                          onClick={() => nav(`/debtors/${debtorId}/reports/${r.id}`)}
                          testid={`reports-tab-action-view-${r.id}`} />
                        <IconAction Icon={Download} label="Download PDF"
                          onClick={async () => {
                            try { await downloadAuthFile(`/reports/${r.id}/pdf`); }
                            catch (e) { toast.error(formatApiError(e, "Couldn't download PDF.")); }
                          }}
                          testid={`reports-tab-action-pdf-${r.id}`} />
                        <IconAction Icon={FileSignature} label="Download Attorney Memo"
                          onClick={async () => {
                            try { await downloadAuthFile(`/reports/${r.id}/attorney-memo`); }
                            catch (e) { toast.error(formatApiError(e, "Couldn't download memo.")); }
                          }}
                          testid={`reports-tab-action-memo-${r.id}`} />
                      </>
                    )}
                  </div>
                </td>
              </motion.tr>
            );
          })}
        </tbody>
      </table>
    </JeaCard>
  );
};

export default ReportsTab;
